import { useEffect, useMemo, useState, type CSSProperties } from 'react';

/**
 * Normalizes user input into an absolute URL when possible (for favicon + links).
 */
export function normalizeWebsiteUrl(raw: string | undefined): string | undefined {
  const t = raw?.trim();
  if (!t) return undefined;
  try {
    if (/^https?:\/\//i.test(t)) {
      const u = new URL(t);
      return u.href;
    }
    return new URL(`https://${t}`).href;
  } catch {
    return undefined;
  }
}

export const getFaviconUrl = (websiteUrl: string | undefined): string | null => {
  const normalized = websiteUrl ? normalizeWebsiteUrl(websiteUrl) : undefined;
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=128`;
  } catch {
    return null;
  }
};

interface FaviconProps {
  domain?: string;
  websiteUrl?: string;
  size?: number;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onError?: () => void;
}

function faviconPlaceholder(size: number, className?: string, style?: CSSProperties) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: Math.max(4, Math.round(size * 0.3)),
        background: 'var(--global-separator-color)',
        flexShrink: 0,
        ...style,
      }}
      aria-hidden
    />
  );
}

function resolveFaviconDomain(domain?: string, websiteUrl?: string): string | null {
  if (domain) return domain;
  if (!websiteUrl) return null;
  const n = normalizeWebsiteUrl(websiteUrl);
  if (!n) return null;
  try {
    return new URL(n).hostname;
  } catch {
    return null;
  }
}

export function Favicon({
  domain,
  websiteUrl,
  size = 12,
  alt = 'Website favicon',
  className,
  style,
  onError,
}: FaviconProps) {
  const faviconDomain = useMemo(
    () => resolveFaviconDomain(domain, websiteUrl),
    [domain, websiteUrl]
  );
  const [sourceIndex, setSourceIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSourceIndex(0);
    setFailed(false);
  }, [faviconDomain]);

  if (!faviconDomain) return null;

  const sources = [
    `https://www.google.com/s2/favicons?domain=${faviconDomain}&sz=128`,
    `https://icons.duckduckgo.com/ip3/${faviconDomain}.ico`,
  ];

  if (failed) {
    if (onError) return null;
    return faviconPlaceholder(size, className, style);
  }

  return (
    <img
      src={sources[sourceIndex]}
      alt={alt}
      width={size}
      height={size}
      className={className}
      style={style}
      onError={() => {
        if (sourceIndex < sources.length - 1) {
          setSourceIndex((i) => i + 1);
          return;
        }
        setFailed(true);
        onError?.();
      }}
    />
  );
}
